import React, {
  useEffect,
  useState
} from "react";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  Input,
  SubmitContainer
} from "../../../assets/GlobalStyles";
import styled from "styled-components";
import {
  Controller,
  useFormContext
} from "react-hook-form";
import {
  useAtom,
  useSetAtom
} from "jotai";
import {useResetAtom} from "jotai/utils";
import {toast} from "react-toastify";
import {CampaignStepForm, CampaignStepSidebar} from "./CampaignStepDesign";
import CampaignSettingSummary from "../CampaignSettingSummary";

import {SearchAdvertiser} from "../../../components/common/SearchAdvertiser";
import {TemporaryListModal} from "../../../components/campaign/TemporaryListModal";
import {stepCampaignAtom} from "../entity";
import {
  campaignBasicInfoAtom,
  campaignTemporaryListAtom
} from "../entity/Info";
import {campaignBudgetInfoAtom} from "../entity/Budget";
import {
  resistCampaignBasic,
  selBasicInfo,
  selTemporaryList
} from "../../../services/campaign/InfoAxios";

const PRODUCT_TYPE = {
  AUDIO: "AUDIO",
  BANNER: "BANNER",
  POP_UNDER: "POP_UNDER"
};

// 목표는 사용자 설정 항목으로 노출하지 않고 생성 시 고정값을 사용한다.
const DEFAULT_CAMPAIGN_GOAL = {
  goalType: "CAMPAIGN_VIEW_GOAL",
  goal: "VIEW_COUNT",
  goalValue: 1
};

const PRODUCT_OPTIONS = [
  {
    value: PRODUCT_TYPE.AUDIO,
    title: "오디오",
    description:
        "오디오 파일을 활용한 광고 캠페인을 생성합니다.",
    imageName: "audio",
    imageAlt: "오디오 캠페인 상품",
    disabled: false
  },
  {
    value: PRODUCT_TYPE.BANNER,
    title: "배너",
    description:
        "이미지 배너를 활용한 광고 캠페인을 생성합니다.",
    imageName: "banner",
    imageAlt: "배너 캠페인 상품",
    disabled: true
  },
  {
    value: PRODUCT_TYPE.POP_UNDER,
    title: "팝언더",
    description:
        "새 창 연결 방식의 광고 캠페인을 생성합니다.",
    imageName: "popunder",
    imageAlt: "팝언더 캠페인 상품",
    disabled: true
  }
];

const getGoalType = goal => {
  if (goal?.startsWith("CONVERSION")) {
    return "CAMPAIGN_CONVERSION_GOAL";
  }

  if (goal?.startsWith("VISIT")) {
    return "CAMPAIGN_VISIT_GOAL";
  }

  return "CAMPAIGN_VIEW_GOAL";
};

export function CampaignOne() {
  const [, setStepCampaign] =
      useAtom(stepCampaignAtom);

  const setCampaignTemporaryList =
      useSetAtom(campaignTemporaryListAtom);

  const [
    campaignBasicInfo,
    setCampaignBasicInfo
  ] = useAtom(campaignBasicInfoAtom);

  const resetBasicInfo =
      useResetAtom(campaignBasicInfoAtom);

  const resetBudgetInfo =
      useResetAtom(campaignBudgetInfoAtom);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    control,
    formState: {errors},
    clearErrors
  } = useFormContext();

  const [advertiserInfo, setAdvertiserInfo] =
      useState(null);

  const [
    temporaryListVisible,
    setTemporaryListVisible
  ] = useState(false);

  const [
    temporaryCampaignLoaded,
    setTemporaryCampaignLoaded
  ] = useState(false);

  const [isSaving, setIsSaving] =
      useState(false);

  useEffect(() => {
    resetBasicInfo();
    resetBudgetInfo();
    reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchAdvertiser =
      async data => {
        if (!data?.id) {
          return;
        }

        setTemporaryCampaignLoaded(false);

        setCampaignBasicInfo(prev => ({
          ...prev,
          userId: data.id,
          username: data.username,
          adverName: data.adverName,
          adverInfo: `${data.adverName ?? "-"}(${data.username ?? "-"})`
        }));

        setAdvertiserInfo({
          userId: data.id,
          username: data.username,
          managerName:
              data.staffName ?? "-",
          advertiserName:
              data.adverName ?? "-"
        });

        setValue(
            "username",
            data.username,
            {
              shouldValidate: true
            }
        );

        clearErrors("username");

        try {
          const response =
              await selTemporaryList(
                  data.id
              );

          const temporaryList =
              response ?? [];

          setCampaignTemporaryList(
              temporaryList
          );

          setTemporaryListVisible(
              temporaryList.length > 0
          );
        } catch (error) {
          console.error(
              "임시 저장 캠페인 조회 실패:",
              error
          );

          setTemporaryListVisible(false);

          toast.error(
              "임시 저장 캠페인을 조회하는 중 오류가 발생했습니다.",
              {
                toastId:
                    "temporaryCampaignLoadError"
              }
          );
        }
      };

  const handleSelectedTemporaryList =
      async data => {
        if (!data?.id) {
          return;
        }

        try {
          const response =
              await selBasicInfo(data.id);

          if (!response) {
            throw new Error(
                "임시 저장 캠페인 응답이 없습니다."
            );
          }

          const loadedInfo = {
            campaignId: data.id,
            pixelId:
                response.pixelId ?? "",
            goal:
                response.goal ??
                "VISIT_CLICK_COUNT",
            goalValue:
                response.goalValue ?? 1,
            goalType:
                getGoalType(
                    response.goal
                ),
            productType:
                response.productType ??
                PRODUCT_TYPE.AUDIO,
            name:
                response.name ?? "",
            step:
                response.step ?? ""
          };

          setCampaignBasicInfo(prev => ({
            ...prev,
            ...loadedInfo
          }));

          setValue(
              "name",
              loadedInfo.name,
              {
                shouldValidate: true,
                shouldDirty: false,
                shouldTouch: false
              }
          );

          setValue(
              "goalValue",
              loadedInfo.goalValue
          );

          clearErrors();
          setTemporaryCampaignLoaded(true);
          setTemporaryListVisible(false);

          toast.success(
              "임시 저장 캠페인을 불러왔습니다.",
              {
                toastId:
                    "temporaryCampaignLoaded",
                autoClose: 1000
              }
          );
        } catch (error) {
          console.error(
              "임시 저장 캠페인 불러오기 실패:",
              error
          );

          toast.error(
              "임시 저장 캠페인을 불러오는 중 오류가 발생했습니다.",
              {
                toastId:
                    "temporaryCampaignDetailError"
              }
          );
        }
      };

  const handleChangeName =
      event => {
        if (temporaryCampaignLoaded) {
          return;
        }

        const {value} = event.target;

        setCampaignBasicInfo(prev => ({
          ...prev,
          name: value
        }));

        clearErrors("name");
      };

  const handleChangeProductType =
      option => {
        if (
            option.disabled ||
            temporaryCampaignLoaded
        ) {
          return;
        }

        setCampaignBasicInfo(prev => ({
          ...prev,
          productType:
          option.value
        }));

        clearErrors("productType");
      };

  const validateProductType = () => {
    if (
        campaignBasicInfo?.productType
    ) {
      return true;
    }

    setError("productType", {
      type: "required",
      message:
          "캠페인 상품을 선택해주세요."
    });

    return false;
  };

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    if (!validateProductType()) {
      return;
    }

    const trimmedName =
        campaignBasicInfo?.name?.trim();

    if (!trimmedName) {
      setError("name", {
        type: "required",
        message:
            "캠페인명을 입력해주세요."
      });

      return;
    }

    setIsSaving(true);

    try {
      /*
       * 임시 저장 캠페인처럼 이미 생성된 캠페인은
       * 현재 정보를 그대로 사용하고 다음 단계로 이동한다.
       */
      if (
          temporaryCampaignLoaded ||
          campaignBasicInfo?.campaignId
      ) {
        setCampaignBasicInfo(prev => ({
          ...prev,
          name: trimmedName
        }));

        resetBudgetInfo();

        setStepCampaign({
          steps: 1
        });

        return;
      }

      const response =
          await resistCampaignBasic({
            ...campaignBasicInfo,
            name: trimmedName,
            ...DEFAULT_CAMPAIGN_GOAL,
            pixelId:
                "10f0a15a-c8c0-48ed-809c-ee643b05ad05"
          });

      if (!response?.value) {
        throw new Error(
            "캠페인 기본 정보 저장 실패"
        );
      }

      setCampaignBasicInfo(prev => ({
        ...prev,
        name: trimmedName,
        campaignId:
        response.value,
        step: "INIT"
      }));

      resetBudgetInfo();

      toast.success(
          "캠페인 기본 정보가 저장되었습니다.",
          {
            toastId:
                "campaignBasicInfoSaved",
            autoClose: 800
          }
      );

      setStepCampaign({
        steps: 1
      });
    } catch (error) {
      console.error(
          "캠페인 기본 정보 저장 실패:",
          error
      );

      toast.error(
          "캠페인 기본 정보를 저장하는 중 오류가 발생했습니다.",
          {
            toastId:
                "campaignBasicInfoSaveError"
          }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onError = formErrors => {
    console.error(
        "캠페인 기본 정보 입력값 확인:",
        formErrors
    );
  };

  return (
      <CampaignStepForm
          onSubmit={handleSubmit(
              onSubmit,
              onError
          )}
      >
        <Board>
          <BoardHeader>
            캠페인 생성
          </BoardHeader>

          <BoardSearchResult>
            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  광고주 및 캠페인 정보
                </SectionTitle>

                <SectionGuide>
                  캠페인을 생성할 광고주와
                  캠페인명을 설정합니다.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <FieldRow>
                  <FieldLabel>
                    광고주 설정
                  </FieldLabel>

                  <FieldContent>
                    <AdvertiserSearchRow>
                      <AdvertiserInputWrap>
                        <AdvertiserInput
                            type="text"
                            readOnly
                            placeholder="광고주를 검색해주세요."
                            value={
                                campaignBasicInfo
                                    ?.username ?? ""
                            }
                            {...register(
                                "username",
                                {
                                  required:
                                      "광고주를 검색해주세요."
                                }
                            )}
                        />
                      </AdvertiserInputWrap>

                      <SearchAdvertiser
                          title="검색"
                          onSubmit={
                            handleSearchAdvertiser
                          }
                      />
                    </AdvertiserSearchRow>

                    {errors.username && (
                        <ValidationMessage>
                          {
                            errors.username
                                .message
                          }
                        </ValidationMessage>
                    )}

                    {advertiserInfo && (
                        <AdvertiserSummary>
                          <AdvertiserSummaryItem>
                            <SummaryLabel>
                              광고주명
                            </SummaryLabel>

                            <SummaryValue>
                              {
                                advertiserInfo
                                    .advertiserName
                              }
                            </SummaryValue>
                          </AdvertiserSummaryItem>

                          <AdvertiserSummaryItem>
                            <SummaryLabel>
                              광고주 아이디
                            </SummaryLabel>

                            <SummaryValue>
                              {
                                advertiserInfo
                                    .username
                              }
                            </SummaryValue>
                          </AdvertiserSummaryItem>

                          <AdvertiserSummaryItem>
                            <SummaryLabel>
                              담당자
                            </SummaryLabel>

                            <SummaryValue>
                              {
                                advertiserInfo
                                    .managerName
                              }
                            </SummaryValue>
                          </AdvertiserSummaryItem>
                        </AdvertiserSummary>
                    )}

                  </FieldContent>
                </FieldRow>

                <FieldRow>
                  <FieldLabel>
                    <FieldLabelTitle>
                      캠페인명

                      {temporaryCampaignLoaded && (
                          <LockedBadge>
                            변경 불가
                          </LockedBadge>
                      )}
                    </FieldLabelTitle>
                  </FieldLabel>

                  <FieldContent>
                    <Controller
                        name="name"
                        control={control}
                        rules={{
                          required:
                              "캠페인명을 입력해주세요.",
                          validate: value =>
                              Boolean(
                                  value?.trim()
                              ) ||
                              "공백만 입력할 수 없습니다."
                        }}
                        render={({field}) => (
                            <CampaignNameInput
                                {...field}
                                type="text"
                                maxLength={100}
                                readOnly={
                                  temporaryCampaignLoaded
                                }
                                aria-readonly={
                                  temporaryCampaignLoaded
                                }
                                $locked={
                                  temporaryCampaignLoaded
                                }
                                placeholder="캠페인명을 입력해주세요."
                                value={
                                    campaignBasicInfo
                                        ?.name ?? ""
                                }
                                onChange={event => {
                                  if (
                                      temporaryCampaignLoaded
                                  ) {
                                    return;
                                  }

                                  field.onChange(
                                      event
                                  );

                                  handleChangeName(
                                      event
                                  );
                                }}
                            />
                        )}
                    />

                    <InputGuide>
                      {temporaryCampaignLoaded
                          ? "임시 저장 캠페인의 캠페인명은 변경할 수 없습니다."
                          : "예: 캠페인명_광고주명_년월일"}
                    </InputGuide>

                    {errors.name && (
                        <ValidationMessage>
                          {
                            errors.name
                                .message
                          }
                        </ValidationMessage>
                    )}
                  </FieldContent>
                </FieldRow>
              </InnerBox>
            </SettingCard>

            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  캠페인 상품 설정
                </SectionTitle>

                <SectionGuide>
                  생성할 캠페인의 광고 상품을
                  선택합니다.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <FieldRow>
                  <FieldLabel>
                    <FieldLabelTitle>
                      캠페인 상품

                      {temporaryCampaignLoaded && (
                          <LockedBadge>
                            변경 불가
                          </LockedBadge>
                      )}
                    </FieldLabelTitle>
                  </FieldLabel>

                  <FieldContent>
                    <ProductGrid>
                      {PRODUCT_OPTIONS.map(
                          option => {
                            const active =
                                campaignBasicInfo
                                    ?.productType ===
                                option.value;

                            const disabled =
                                option.disabled ||
                                temporaryCampaignLoaded;

                            return (
                                <ProductButton
                                    key={
                                      option.value
                                    }
                                    type="button"
                                    $active={
                                      active
                                    }
                                    $locked={
                                      temporaryCampaignLoaded
                                    }
                                    disabled={
                                      disabled
                                    }
                                    onClick={() =>
                                        handleChangeProductType(
                                            option
                                        )
                                    }
                                >
                                  <ProductImageRow>
                                    <ProductImageBox
                                        $active={
                                          active
                                        }
                                    >
                                      <ProductImage
                                          src={`../assets/images/campaign/img_${option.imageName}_${active ? "on" : "off"}.png`}
                                          alt={
                                            option.imageAlt
                                          }
                                      />
                                    </ProductImageBox>

                                    <ProductRadio
                                        $active={
                                          active
                                        }
                                    />
                                  </ProductImageRow>

                                  <ProductInfoRow>
                                    <ProductTitle>
                                      {
                                        option.title
                                      }

                                      {option.disabled && (
                                          <DisabledBadge>
                                            준비 중
                                          </DisabledBadge>
                                      )}
                                    </ProductTitle>

                                    <ProductDescription>
                                      {
                                        option.description
                                      }
                                    </ProductDescription>
                                  </ProductInfoRow>
                                </ProductButton>
                            );
                          }
                      )}
                    </ProductGrid>

                    {temporaryCampaignLoaded && (
                        <InputGuide>
                          임시 저장 캠페인의 상품 유형은
                          변경할 수 없습니다.
                        </InputGuide>
                    )}

                    {errors.productType && (
                        <ValidationMessage>
                          {
                            errors.productType
                                .message
                          }
                        </ValidationMessage>
                    )}
                  </FieldContent>
                </FieldRow>
              </InnerBox>
            </SettingCard>
          </BoardSearchResult>
          {temporaryCampaignLoaded && (
              <LoadedCampaignNotice>
                <LoadedBadge>
                  임시 저장
                </LoadedBadge>

                불러온 캠페인 정보를 기준으로
                다음 단계를 진행합니다.
              </LoadedCampaignNotice>
          )}
        </Board>

        <CampaignStepSidebar>
          <CampaignSettingSummary currentStep={0} />
          <SubmitContainer>
          <NextButton
              type="submit"
              disabled={isSaving}
          >
            {isSaving
                ? "처리 중"
                : "다음[1/4]"}
          </NextButton>
          </SubmitContainer>
        </CampaignStepSidebar>

        {temporaryListVisible && (
            <TemporaryListModal
                userId={
                  campaignBasicInfo?.userId
                }
                onClose={() =>
                    setTemporaryListVisible(
                        false
                    )
                }
                onSubmit={
                  handleSelectedTemporaryList
                }
            />
        )}
      </CampaignStepForm>
  );
}

const SettingCard = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: 15px;
  font-weight: 700;
`;

const SectionGuide = styled.div`
  color: #888;
  font-size: 12px;
  line-height: 1.5;
  text-align: right;
`;

const InnerBox = styled.div`
  padding: 22px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FieldLabel = styled.div`
  width: 150px;
  min-width: 150px;
  padding-top: 10px;
  color: #444;
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldLabelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
`;

const LockedBadge = styled.span`
  display: inline-flex;
  min-height: 20px;
  align-items: center;
  padding: 0 7px;
  border: 1px solid #e1e1e1;
  border-radius: 10px;
  background-color: #f1f1f1;
  color: #888;
  font-size: 9px;
  font-weight: 600;
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 9px;
`;

const AdvertiserSearchRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AdvertiserInputWrap = styled.div`
  width: 100%;
  min-width: 340px;
  max-width: 460px;

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const AdvertiserInput = styled(Input)`
  width: 100%;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #555;

  &:focus {
    border-color: #f5811f;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const CampaignNameInput = styled(Input)`
  width: 100%;
  min-width: 340px;
  max-width: 620px;
  height: 40px;
  border: 1px solid
    ${({$locked}) =>
    $locked
        ? "#e1e1e1"
        : "#e5e5e5"};
  border-radius: 6px;
  background-color:
    ${({$locked}) =>
    $locked
        ? "#f5f5f5"
        : "#fff"};
  color:
    ${({$locked}) =>
    $locked
        ? "#666"
        : "inherit"};
  cursor:
    ${({$locked}) =>
    $locked
        ? "not-allowed"
        : "text"};

  &:focus {
    border-color:
      ${({$locked}) =>
    $locked
        ? "#e1e1e1"
        : "#f5811f"};
  }

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const InputGuide = styled.div`
  color: #999;
  font-size: 11px;
  line-height: 1.4;
`;

const AdvertiserSummary = styled.div`
  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));
  overflow: hidden;
  margin-top: 4px;
  border: 1px solid #e5e5e5;
  border-radius: 7px;
  background-color: #fff;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const AdvertiserSummaryItem = styled.div`
  min-width: 0;
  padding: 13px 15px;
  border-right: 1px solid #eee;

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 800px) {
    border-right: 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const SummaryLabel = styled.div`
  margin-bottom: 5px;
  color: #999;
  font-size: 10px;
`;

const SummaryValue = styled.div`
  overflow: hidden;
  color: #444;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoadedCampaignNotice = styled.div`
  display: flex;
  min-height: 38px;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border-left: 3px solid #f5811f;
  border-radius: 0 5px 5px 0;
  background-color: #fff8f1;
  color: #777;
  font-size: 11px;
`;

const LoadedBadge = styled.span`
  display: inline-flex;
  min-height: 22px;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 8px;
  border-radius: 11px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 10px;
  font-weight: 700;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProductButton = styled.button`
  display: flex;
  min-height: 190px;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  overflow: hidden;
  border: 1px solid
  ${({$active}) =>
    $active
        ? "#f5811f"
        : "#e5e5e5"};
  border-radius: 8px;
  background-color:
          ${({$active}) =>
    $active
        ? "#fff8f1"
        : "#fff"};
  text-align: left;
  cursor: pointer;
  transition:
          border-color 0.2s ease,
          background-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    background-color: #fffaf6;
  }

  &:disabled {
    border-color:
      ${({$active}) =>
    $active
        ? "#f5811f"
        : "#e9e9e9"};
    background-color:
      ${({$active}) =>
    $active
        ? "#fff8f1"
        : "#f5f5f5"};
    cursor: not-allowed;
    opacity:
      ${({$active, $locked}) =>
    $locked && $active
        ? 1
        : 0.58};
  }
`;

const ProductImageRow = styled.span`
  position: relative;
  display: flex;
  min-height: 126px;
  align-items: center;
  justify-content: center;
  padding: 18px;
  border-bottom: 1px solid #eeeeee;
  background-color: #fafafa;
`;

const ProductImageBox = styled.span`
  display: inline-flex;
  width: 204px;
  height: 188px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid
  ${({$active}) =>
          $active
                  ? "#f1d4c2"
                  : "#e8e8e8"};
  border-radius: 12px;
  background-color:
          ${({$active}) =>
                  $active
                          ? "#fff4e8"
                          : "#fff"};
`;

const ProductImage = styled.img`
  display: block;
  width: 184px;
  height: 172px;
  object-fit: contain;
`;

const ProductInfoRow = styled.span`
  display: flex;
  min-height: 72px;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background-color: #fff;
`;

const ProductTitle = styled.span`
  display: flex;
  align-items: center;5597
  gap: 7px;
  color: #333;
  font-size: 14px;
  font-weight: 700;
`;

const ProductDescription = styled.span`
  color: #888;
  font-size: 11px;
  line-height: 1.4;
`;

const DisabledBadge = styled.span`
  display: inline-flex;
  min-height: 20px;
  align-items: center;
  padding: 0 7px;
  border-radius: 10px;
  background-color: #e9e9e9;
  color: #999;
  font-size: 9px;
  font-weight: 600;
`;
const ProductRadio = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1px solid
  ${({$active}) =>
          $active
                  ? "#f5811f"
                  : "#ccc"};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color:
            ${({$active}) =>
                    $active
                            ? "#f5811f"
                            : "transparent"};
    content: "";
    transform: translate(-50%, -50%);
  }
`;

const ValidationMessage = styled.div`
  color: #e03131;
  font-size: 11px;
  line-height: 1.4;
`;

const NextButton = styled.button`
  min-width: 104px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
